import { useCallback, useEffect, useMemo, useState } from 'react'
import { IAllProps } from '@tinymce/tinymce-react'
import styled from 'styled-components'

import TinyMCE from './TinyMCE'

import './style.css'

type Props = {
  handleChange: (value: string) => void
  isReadOnly?: boolean
  reInitTriggerArrray?: any[] // component specific re-init triggers
  maxDropFileSize?: number | null
} & IAllProps

export const TinyMceEditer = ({
  handleChange,
  isReadOnly = false,
  init,
  onInit: customHanleOnInit,
  reInitTriggerArrray = [],
  maxDropFileSize = 5 * 1024 * 1024,
  ...props
}: Props) => {
  const [reInitTriggerKey, setReInitTriggerKey] = useState<number>(0)
  const [content, setContent] = useState<string>('')

  useEffect(() => {
    setReInitTriggerKey((prev) => prev + 1)
  }, [isReadOnly, ...reInitTriggerArrray])

  const initValue: IAllProps['init'] = useMemo(
    () => ({
      ...init,
      relative_urls: false,
      remove_script_host: false,
      highlight_on_focus: false,
      document_base_url:
        process.env.NODE_ENV == 'development' ? process.env.APP_URL : String(process.env.API_URL).split('/api')[0],
      link_context_toolbar: !isReadOnly,
      skin_url: '/lib/tinymce/skins/ui/oxide',
      content_css: '/lib/tinymce/skins/content/default/content.css',
      height: 500,
      menubar: false,
      plugins: [
        'advlist',
        'anchor',
        'autolink',
        'help',
        'image',
        'link',
        'lists',
        'searchreplace',
        'table',
        'emoticons',
        'code',
        ...(init?.plugins ?? []),
      ],
      contextmenu: false,
      toolbar: isReadOnly ? false : init?.toolbar ?? 'bold italic underline link unlink removeformat fontsize ',
      file_picker_types: 'image ' + init?.file_picker_types,
      images_file_types: 'jpg,svg,webp,png',
      font_size_input_default_unit: 'px',
      font_size_formats: '8px 10px 12px 14px 16px 18px 24px 36px 48px',
      content_style: `
          body { font-family:Helvetica,Arial,sans-serif; font-size:14px; }
          p {
            margin: 0;
            padding: 0;
          }
          h1, h2, h3, h4, h5, h6 {
              margin: 0;
              padding: 0;
          }
         ${isReadOnly ? `a { cursor: pointer;}` : ''}
        `,
      statusbar: false,
      setup: (editor) => {
        editor.on('drop', (e: DragEvent) => {
          const files = e.dataTransfer?.files
          if (!files?.length || maxDropFileSize == null) return

          for (const file of Array.from(files)) {
            if (file.size > maxDropFileSize) {
              e.preventDefault()
              e.stopPropagation()
              editor.notificationManager.open({
                text: `File too large. Maximum allowed size is ${maxDropFileSize / 1024 / 1024}MB.`,
                type: 'error',
                timeout: 4000,
              })
              return
            }
          }
        })

        if (typeof init?.setup === 'function') init.setup(editor)
      },
    }),
    [init, isReadOnly, maxDropFileSize],
  )

  const onChange = useCallback(
    (value: string) => {
      setContent(value)
      handleChange(value)
    },
    [handleChange, setContent],
  )

  const handleEditorInit: IAllProps['onInit'] = useCallback(
    (evt, editor) => {
      // common re-init handlers goes here
      editor.on('click', (e: any) => {
        e.stopPropagation()
        const target = e.target as HTMLElement

        const clickedElement =
          target.tagName == 'A' ? target : target?.parentElement?.tagName == 'A' ? target.parentElement : target

        if (isReadOnly && clickedElement?.tagName === 'A') {
          e.preventDefault()
          const href = clickedElement.getAttribute('href')
          const linkTarget = clickedElement.getAttribute('target') || '_blank'

          if (href) {
            window.open(href, linkTarget)
          }
        }
      })

      // component specific re-init handlers goes here
      if (customHanleOnInit) customHanleOnInit(evt, editor)
    },
    [isReadOnly, customHanleOnInit],
  )

  return (
    <Continer>
      <TinyMCE
        key={reInitTriggerKey}
        onInit={handleEditorInit}
        value={content}
        onEditorChange={onChange}
        disabled={isReadOnly}
        init={{
          ...initValue,
          editable_root: !isReadOnly,
        }}
        {...props}
      />
    </Continer>
  )
}

const Continer = styled.div`
  .tox-tinymce {
    border: none;
    border-radius: 4px;
    max-height: 50vh !important;
  }

  .tox-editor-header {
    box-shadow: none !important;
    padding: 3px 0 !important;
    border-bottom: 1px solid ${(p) => p.theme.colors.gray3} !important;
  }

  .tox {
    .tox *:not(svg):not(rect) {
      .tox-icon .tox-tbtn__icon-wrap {
        display: inline-flex !important;
      }
    }

    .tox-tbtn {
      margin: 0 4px;
      width: auto;
      cursor: pointer;

      > span {
        display: inline-flex !important;
      }

      &--select {
        width: 57px;
        margin: 0 6px;
        cursor: pointer;
        box-sizing: content-box;

        .tox-tbtn__select-label {
          margin: 0;
          line-height: 17px;
          height: 18px;
          font-weight: 500;
          cursor: pointer;
        }
      }
    }

    .tox-toolbar {
      &__group {
        gap: 3px;
      }

      &-overlord > .tox-toolbar__primary {
        background-size: 0;
      }

      &--scrolling {
        background: ${(p) => p.theme.colors.gray1};
      }
    }
  }

  .tox .tox-edit-area {
    &__iframe {
      background-color: ${(p) => p.theme.colors.gray1};
    }
  }
`

export default TinyMceEditer
